import { useEffect, useState } from "react";
import { LayoutGrid, TriangleAlert } from "lucide-react";
import { toast } from "sonner";
import { Modal } from "@/components/Modals";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import * as layout from "@/lib/layoutApi";

const SHAPES = ["rect", "round"];

/**
 * Places tables on the floor plan.
 *
 * "Open floor designer" was a button with no onClick. The geometry it edits
 * (pos_x, pos_y, shape, width, height) landed in migration 038 and is served by
 * /api/floor/layout.
 *
 * A numeric grid rather than drag-and-drop: this is configured once at setup on
 * whatever machine is to hand, and a form works on a tablet without a mouse. The
 * save is all-or-nothing server-side, so a rejected row leaves the plan untouched
 * rather than half-applied.
 */
export default function FloorDesigner({ token, onClose }) {
  const [tables, setTables] = useState([]);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    layout
      .getLayout(token)
      .then(rows => setTables(rows))
      .catch(failure => setError(failure?.response?.data?.detail || "Could not load the floor plan"));
  }, [token]);

  const edit = (id, key, value) => {
    setDirty(true);
    setTables(current =>
      current.map(table => (table.id === id ? { ...table, [key]: value } : table))
    );
  };

  const numeric = value => {
    if (value === "" || value === null) return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = tables.map(t => ({
        id: t.id,
        pos_x: numeric(t.pos_x),
        pos_y: numeric(t.pos_y),
        shape: t.shape,
        width: Number(t.width) || 1,
        height: Number(t.height) || 1,
        floor: t.floor || null,
        // Seats: the captain's tile shows it (settings sweep 2026-10-19).
        capacity: Math.min(50, Math.max(1, Number(t.capacity) || 1)),
      }));
      const saved = await layout.saveLayout(payload, token);
      setTables(saved);
      setDirty(false);
      toast.success(`Floor plan saved · ${saved.length} tables`);
    } catch (failure) {
      const detail = failure?.response?.data?.detail;
      toast.error(
        typeof detail === "string" ? detail : detail?.[0]?.msg || "Could not save the floor plan"
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="Floor designer" onClose={onClose} testId="floor-designer-modal">
      {error && (
        <div className="kds-error" role="alert" data-testid="floor-designer-error">
          <TriangleAlert />
          <span>{error}</span>
        </div>
      )}

      <p className="hint">
        <LayoutGrid /> Position is a grid coordinate, not pixels. Leave blank for a table that
        has not been placed yet.
      </p>

      <div className="floor-designer-grid" data-testid="floor-designer-grid">
        <div className="floor-designer-head">
          <span>Table</span><span>Area</span><span>Seats</span><span>X</span><span>Y</span><span>W</span><span>H</span><span>Shape</span>
        </div>
        {tables.map(table => (
          <div className="floor-designer-row" key={table.id} data-testid={`floor-designer-table-${table.id}`}>
            <strong>{table.label || `Table ${table.number}`}</strong>
            <Input value={table.floor || ""} onChange={e => edit(table.id, "floor", e.target.value)} data-testid={`floor-${table.id}-area-input`}/>
            <Input type="number" min="1" max="50" value={table.capacity ?? ""} onChange={e => edit(table.id, "capacity", e.target.value)} aria-label="Seats" data-testid={`floor-${table.id}-seats-input`}/>
            <Input type="number" min="0" value={table.pos_x ?? ""} onChange={e => edit(table.id, "pos_x", e.target.value)} data-testid={`floor-${table.id}-x-input`}/>
            <Input type="number" min="0" value={table.pos_y ?? ""} onChange={e => edit(table.id, "pos_y", e.target.value)} data-testid={`floor-${table.id}-y-input`}/>
            <Input type="number" min="1" value={table.width} onChange={e => edit(table.id, "width", e.target.value)} data-testid={`floor-${table.id}-w-input`}/>
            <Input type="number" min="1" value={table.height} onChange={e => edit(table.id, "height", e.target.value)} data-testid={`floor-${table.id}-h-input`}/>
            <select value={table.shape} onChange={e => edit(table.id, "shape", e.target.value)} data-testid={`floor-${table.id}-shape-select`}>
              {SHAPES.map(shape => <option value={shape} key={shape}>{shape}</option>)}
            </select>
          </div>
        ))}
      </div>

      {!tables.length && !error && <p className="chart-empty" data-testid="floor-designer-empty">No tables to place.</p>}

      <Button variant="action" className="primary-wide" onClick={save} disabled={saving || !dirty} data-testid="save-floor-layout-button">
        {dirty ? "Save floor plan" : "No changes"}
      </Button>
    </Modal>
  );
}
